import { matchesSearchQuery, normalizeSearchText } from '../../utils/searchUtils';
import { getOperatingOpportunitySummary as getNormalizedOperatingOpportunitySummary } from './clientOperatingOpportunitySummary';

export const DEFAULT_PAGE_SIZE = 25;
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
export const SORT_DIRECTIONS = {
    ASC: 'asc',
    DESC: 'desc',
};
export const COLUMN_WIDTH_LIMITS = {
    MIN: 104,
    MAX: 480,
};

export const DEFAULT_VISIBLE_COLUMNS = {
    all_vehicles: [
        'date',
        'vehicle_model',
        'Total Number Of Vehicles',
        'Number of operating vehicles',
        'Fleet Usage Rate',
        'Total Distance Travelled',
        'Savings (Actual)',
        'Actual CO2 Emissions Saved (Kgs)',
    ],
    per_vehicle: [
        'vehicle_name',
        'license_plate',
        'chassis_number',
        'date',
        'isworkingday',
        'Mileage Difference (km)',
        'SoC Difference (%)',
        'Energy Consumption (kWh)',
        'consumption',
    ],
    per_vehicle_summary: [
        'vehicle_name',
        'license_plate',
        'chassis_number',
        'Total Mileage Travelled (km)',
        'consumption',
        'Total Energy Consumption (kWh)',
        'EV Spending (EGP)',
        'ICE Spending (EGP)',
        'Savings (EGP)',
        'CO2 Emissions Saved (kg)',
        'Trees To Be Planted',
        'Average Speed (km/hr)',
        'Max Speed (km/hr)',
    ],
    driving_behavior: [
        'vehicle_name',
        'license_plate',
        'chassis_number',
        'driving_behavior_score',
        'behavior_status',
        'total_mileage_km',
        'moving_average_speed_km_hr',
        'moving_p50_speed_km_hr',
        'moving_p90_speed_km_hr',
        'moving_p95_speed_km_hr',
        'max_speed_km_hr',
    ],
};

const COLUMN_SELECTION_STORAGE_PREFIX = 'clientOperatingAnalysis.visibleColumns';
const COLUMN_WIDTH_STORAGE_PREFIX = 'clientOperatingAnalysis.columnWidths';
const COLUMN_SELECTION_STORAGE_VERSION = {
    per_vehicle: 'v2',
    per_vehicle_summary: 'v2',
    driving_behavior: 'v2',
};
const COLUMN_WIDTH_STORAGE_VERSION = {
    all_vehicles: 'v1',
    per_vehicle: 'v1',
    per_vehicle_summary: 'v1',
    driving_behavior: 'v1',
};

const VEHICLE_LEVEL_ANALYSIS_TYPES = new Set(['per_vehicle', 'per_vehicle_summary', 'driving_behavior']);
const MONTH_SEARCH_PATTERN = /\b(?:jan|january|feb|february|mar|march|apr|april|may|jun|june|jul|july|aug|august|sep|sept|september|oct|october|nov|november|dec|december)\b|يناير|فبراير|مارس|ابريل|مايو|يونيو|يوليو|اغسطس|سبتمبر|اكتوبر|نوفمبر|ديسمبر/;

const COLUMN_LABELS = {
    vehicle_name: 'Vehicle',
    car_name: 'Vehicle',
    license_plate: 'License plate',
    chassis_number: 'Chassis',
    date: 'Date',
    isworkingday: 'Working day',
    isweekend: 'Weekend',
    vehicle_model: 'Model',
    client: 'Client',
    effective_custody: 'Custody',
    'Fleet Usage Rate': 'Fleet usage',
    'Total Number Of Vehicles': 'Total vehicles',
    'Number of operating vehicles': 'Operating vehicles',
    'Total Distance Travelled': 'Distance',
    'Savings (Actual)': 'Savings',
    'Actual CO2 Emissions Saved (Kgs)': 'CO2 saved',
    'Mileage Difference (km)': 'Mileage delta',
    'Cumulative Mileage (Custody Rebased) (Km)': 'Cumulative mileage',
    'SoC Difference (%)': 'SoC change',
    'Energy Consumption (kWh)': 'Energy',
    'Total Energy Consumption (kWh)': 'Total energy',
    consumption: 'Consumption',
    'Year S': 'Years',
    'Month S': 'Months',
    Yearly: 'Year',
    Monthly: 'Month',
    Quarterly: 'Quarter',
    'Month Year': 'Month/year',
    'Quarter Year': 'Quarter/year',
    'Non-Operating Vehicle Count': 'Non-operating vehicles',
    'Number Of Operating Days': 'Operating days',
    driving_behavior_score: 'Behavior score',
    behavior_status: 'Status',
    trip_sample_count: 'Trip samples',
    moving_sample_count: 'Moving samples',
    stopped_sample_count: 'Stopped samples',
    raw_sample_count: 'Raw samples',
    total_mileage_km: 'Mileage',
    total_soc_delta_pct: 'SoC change',
    total_energy_consumption_kwh: 'Energy',
    weighted_consumption_wh_per_km: 'Consumption',
    weighted_kwh_per_km: 'Energy per km',
    trip_weighted_average_speed_km_hr: 'Trip-weighted speed',
    trip_weighted_moving_average_speed_km_hr: 'Trip-weighted moving speed',
    stopped_sample_ratio: 'Stopped ratio',
    moving_average_speed_km_hr: 'Average speed',
    moving_p50_speed_km_hr: 'P50 speed',
    moving_p75_speed_km_hr: 'P75 speed',
    moving_p90_speed_km_hr: 'P90 speed',
    moving_p95_speed_km_hr: 'P95 speed',
    max_speed_km_hr: 'Max speed',
    moving_speed_stddev: 'Moving speed variability',
    high_speed_sample_count: 'High-speed samples',
    excessive_speed_sample_count: 'Excessive-speed samples',
    high_speed_moving_ratio: 'High-speed ratio',
    excessive_speed_moving_ratio: 'Excessive-speed ratio',
    valid_acceleration_sample_count: 'Acceleration samples',
    moving_average_acceleration: 'Moving acceleration',
    moving_average_positive_acceleration: 'Positive acceleration',
    moving_p75_positive_acceleration: 'P75 positive acceleration',
    moving_p95_positive_acceleration: 'P95 positive acceleration',
    moving_max_positive_acceleration: 'Max positive acceleration',
    moving_average_abs_braking: 'Average braking',
    moving_p75_abs_braking: 'P75 braking',
    moving_p95_abs_braking: 'P95 braking',
    moving_max_abs_braking: 'Max braking',
    harsh_acceleration_count: 'Harsh acceleration events',
    harsh_braking_count: 'Harsh braking events',
    harsh_acceleration_per_100km: 'Harsh acceleration / 100 km',
    harsh_braking_per_100km: 'Harsh braking / 100 km',
    valid_jerk_sample_count: 'Jerk samples',
    moving_average_abs_jerk: 'Average jerk',
    moving_p75_abs_jerk: 'P75 jerk',
    moving_p95_abs_jerk: 'P95 jerk',
    moving_max_abs_jerk: 'Max jerk',
    'Max Range': 'Max range',
    'Fleet Full Utilization Distance': 'Full-fleet utilization distance',
    'Avg Distance Travelled': 'Average distance',
    '75 Percentile Distance Travelled': '75th percentile distance',
    '25 Percentile Distance Travelled': '25th percentile distance',
    'Total EV Consumption': 'Total EV consumption',
    'Avg EV Consumption': 'Average EV consumption',
    '75 Percentile EV Consumption': '75th percentile EV consumption',
    '25 Percentile EV Consumption': '25th percentile EV consumption',
    'Actual ICE Spending': 'Actual fuel spending',
    'Actual EV Spending': 'Actual electric spending',
    'EV Spending (EGP)': 'Electric spending',
    'ICE Spending (EGP)': 'Fuel spending',
    'Savings (EGP)': 'Savings',
    'Savings % (Actual)': 'Savings %',
    'Actual Number Of Trees': 'Trees saved',
    'CO2 Emissions Saved (kg)': 'CO2 saved',
    'Trees To Be Planted': 'Trees saved',
    'Actual CO2 Emissions Of All Fleet (Kgs)': 'Fleet CO2 emissions',
    'Max Range ALL Fleet CO2 Emissions (Kgs)': 'Max-range fleet CO2 emissions',
    'Max Mileage Range ICE Spending Of Operating Vehicles': 'Max-range fuel spending, operating vehicles',
    'Max Mileage Range EV Spending Of Operating Vehicles': 'Max-range electric spending, operating vehicles',
    'Savings (Max Operating Fleet)': 'Max operating fleet savings',
    'Savings % (Max Operating Fleet)': 'Max operating fleet savings %',
    'Max Range Operating Vehicles CO2 Emissions Saved (Kgs)': 'Max-range operating CO2 saved',
    'Max Range Operating Number Of Trees': 'Max-range operating trees saved',
    'Max Mileage Range ICE Spending Of All Fleet': 'Max-range fuel spending, all fleet',
    'Max Mileage Range EV Spending Of All Fleet': 'Max-range electric spending, all fleet',
    'Savings (Max Full Fleet)': 'Max full-fleet savings',
    'Savings % (Max Full Fleet)': 'Max full-fleet savings %',
    'Max Range ALL CO2 Emissions Saved (Kgs)': 'Max-range all-fleet CO2 saved',
    'Max Range All Fleet Number Of Trees': 'Max-range all-fleet trees saved',
    'Opex Emissions': 'Operating emissions',
    'Opex Savings': 'Operating savings',
    'New Price': 'New price',
    'Old Price': 'Old price',
    'Avioded Purchase': 'Avoided purchase',
    'Total Capex Savings': 'Total capital savings',
    'Chassis Emissions': 'Chassis emissions',
    'Life Time': 'Lifetime',
    'Savings 75% Vehicles': 'Savings at 75% vehicles',
    'Savings 50% Vehicles': 'Savings at 50% vehicles',
    'Savings 100% Vehicles': 'Savings at 100% vehicles',
    '100% Vehicle Savings': '100% vehicle savings',
    '75% Vehicle Savings': '75% vehicle savings',
    '50% Vehicle Savings': '50% vehicle savings',
    'Total Number Of Fleet': 'Total fleet vehicles',
    '100% Vehicles ICE Spending': '100% vehicles fuel spending',
    '100% Vehicles EV Spending': '100% vehicles electric spending',
    'EV CPK': 'Electric cost per km',
    'ICE CPK': 'Fuel cost per km',
    'Number Of Non Operating Vehicles (Complaint)': 'Non-operating vehicles with complaints',
    'Number Of Non Operating Days (Complaint)': 'Non-operating complaint days',
};

const DEFAULT_FORMAT_COPY = {
    booleans: {
        yes: 'Yes',
        no: 'No',
    },
    units: {
        currencyPrefix: 'EGP',
        km: 'km',
        kwh: 'kWh',
        whPerKm: 'Wh/km',
        kg: 'kg',
    },
    summary: {
        rows: 'Rows',
        cache: 'Cache',
        refreshed: 'Refreshed',
    },
    statuses: {
        ready: 'Ready',
        stale: 'Stale',
        excellent: 'Excellent',
        normal: 'Normal',
        watchlist: 'Watchlist',
        needs_attention: 'Needs attention',
        insufficient_data: 'Insufficient data',
        unknown: 'Unknown',
    },
    table: {
        showingRange: 'Showing {start}-{end} of {total}',
        showingEmpty: 'Showing 0 of 0',
    },
    impact: {
        title: 'EV savings impact',
        actualSavings: 'Actual savings',
        evDistance: 'EV distance driven',
        co2Saved: 'CO2 saved',
        treeEquivalent: 'Tree-equivalent impact',
        viewOpportunity: 'View opportunity savings',
        opportunity: 'Opportunity',
        maxOperatingSavings: 'Max operating fleet savings',
        additionalOpportunity: 'Additional opportunity',
        operatingVehiclesBasis: 'Operating vehicles basis',
        opportunityCaptured: '{percent}% captured',
        opportunityRemaining: '{percent}% remaining',
        opportunityProgressTitle: 'Savings progress',
        opportunityTarget: 'Target: {value}',
        opportunityActualValue: '{value} actual',
        opportunityRemainingValue: '{value} remaining opportunity',
        notAvailable: 'Not available',
        opportunityUnavailable: 'Max operating fleet data is not available for this period.',
    },
};

const IMPACT_METRIC_COLUMNS = [
    {
        key: 'actualSavings',
        copyKey: 'actualSavings',
        actualKey: 'actual_savings_egp',
        fallbackColumn: 'Savings (Actual)',
        fallbackLabel: 'Actual savings',
    },
    {
        key: 'evDistance',
        copyKey: 'evDistance',
        actualKey: 'ev_distance_km',
        fallbackColumn: 'Total Distance Travelled',
        fallbackLabel: 'EV distance driven',
    },
    {
        key: 'co2Saved',
        copyKey: 'co2Saved',
        actualKey: 'co2_saved_kg',
        fallbackColumn: 'Actual CO2 Emissions Saved (Kgs)',
        fallbackLabel: 'CO2 saved',
    },
    {
        key: 'treeEquivalent',
        copyKey: 'treeEquivalent',
        actualKey: 'trees_to_be_planted',
        fallbackColumn: 'Actual Number Of Trees',
        fallbackLabel: 'Tree-equivalent impact',
    },
];

const OPPORTUNITY_DETAIL_COLUMNS = {
    actualSavings: 'Savings (Actual)',
    maxOperatingSavings: 'Savings (Max Operating Fleet)',
    additionalOpportunity: 'Savings (Max Operating Fleet)',
    operatingVehiclesBasis: 'Number of operating vehicles',
};

const TEXT_VALUE_COLUMNS = new Set([
    'vehicle_name',
    'car_name',
    'vehicle_label',
    'license_plate',
    'chassis_number',
    'client',
    'vehicle_model',
    'behavior_status',
    'model',
    'make',
]);

const BOOLEAN_COLUMN_PATTERNS = [
    /^is/i,
    /^has/i,
];

const DATE_COLUMN_PATTERNS = [
    /^date$/i,
    /_date$/i,
    /\bdate\b/i,
];

const CURRENCY_COLUMN_PATTERNS = [
    /spending/i,
    /savings/i,
    /price/i,
    /capex/i,
    /opex savings/i,
];

const DISTANCE_COLUMN_PATTERNS = [
    /distance/i,
    /mileage/i,
];

const ENERGY_COLUMN_PATTERNS = [
    /energy.*kwh/i,
];

const EV_CONSUMPTION_RATE_COLUMN_PATTERNS = [
    /^consumption$/i,
    /(^|_)wh_per_km$/i,
    /consumption.*wh.*per.*km/i,
    /^total ev consumption$/i,
    /^(avg|average) ev consumption$/i,
    /^(75|75th) percentile ev consumption$/i,
    /^(25|25th) percentile ev consumption$/i,
];

const COST_PER_KM_COLUMN_PATTERNS = [
    /\bcpk\b/i,
];

const CO2_COLUMN_PATTERNS = [
    /co2/i,
    /emissions/i,
];

const PERCENT_COLUMN_PATTERNS = [
    /%/,
    /ratio/i,
    /usage rate/i,
    /savings %/i,
];

const COUNT_COLUMN_PATTERNS = [
    /count/i,
    /number/i,
    /vehicles/i,
    /days/i,
    /samples?/i,
    /trees/i,
    /range/i,
];

const ONE_DECIMAL_METRIC_COLUMN_PATTERNS = [
    /consumption/i,
    /speed/i,
    /score/i,
    /difference/i,
    /delta/i,
    /avg/i,
    /average/i,
    /percentile/i,
    /\bp\d+\b/i,
    /stddev/i,
    /variability/i,
];

const TOTAL_MAGNITUDE_COLUMN_PATTERNS = [
    /^total\b/i,
    /\btotal\b/i,
    /^total_/i,
    /emissions saved/i,
    /emissions of/i,
];

const getNormalizedColumnKey = (column) => normalizeColumnLabelKey(column);

const isTextColumn = (column) => {
    const columnName = compactWhitespace(column);
    const normalizedColumn = getNormalizedColumnKey(columnName);

    if (TEXT_VALUE_COLUMNS.has(column) || TEXT_VALUE_COLUMNS.has(columnName)) return true;
    if (matchesAny(columnName, DATE_COLUMN_PATTERNS)) return true;
    if (BOOLEAN_COLUMN_PATTERNS.some((pattern) => pattern.test(columnName))) return true;
    return ['year', 'years', 'yearly', 'month', 'months', 'monthly', 'quarter', 'quarterly', 'monthyear', 'quarteryear'].includes(normalizedColumn);
};

const isMetricColumn = (column) => {
    const columnName = compactWhitespace(column);
    return matchesAny(columnName, [
        ...PERCENT_COLUMN_PATTERNS,
        ...CURRENCY_COLUMN_PATTERNS,
        ...ENERGY_COLUMN_PATTERNS,
        ...EV_CONSUMPTION_RATE_COLUMN_PATTERNS,
        ...COST_PER_KM_COLUMN_PATTERNS,
        ...CO2_COLUMN_PATTERNS,
        ...DISTANCE_COLUMN_PATTERNS,
        ...COUNT_COLUMN_PATTERNS,
        ...ONE_DECIMAL_METRIC_COLUMN_PATTERNS,
    ]);
};

const getColumnLayoutType = (column) => {
    const columnName = compactWhitespace(column);
    const normalizedColumn = getNormalizedColumnKey(columnName);

    if (matchesAny(columnName, DATE_COLUMN_PATTERNS)) return 'date';
    if (BOOLEAN_COLUMN_PATTERNS.some((pattern) => pattern.test(columnName))) return 'flag';
    if (normalizedColumn.includes('licenseplate')) return 'plate';
    if (normalizedColumn.includes('chassis')) return 'identity-compact';
    if (TEXT_VALUE_COLUMNS.has(column) || TEXT_VALUE_COLUMNS.has(columnName)) return 'identity';
    if (isMetricColumn(columnName)) return 'metric';
    return 'standard';
};

const COLUMN_PREFERRED_WIDTHS = {
    date: 136,
    flag: 116,
    plate: 180,
    'identity-compact': 132,
    identity: 156,
    metric: 164,
    standard: 148,
};

const compactWhitespace = (value) => String(value || '').replace(/\s+/g, ' ').trim();

const normalizeColumnLabelKey = (value) => compactWhitespace(value)
    .toLowerCase()
    .replace(/[\s_-]+/g, '')
    .replace(/[^\p{L}\p{N}%]+/gu, '');

const isPresent = (value) => value !== null && value !== undefined && value !== '';

const normalizeIdentityValue = (value, { compact = false } = {}) => {
    const normalized = normalizeSearchText(value);
    return compact ? normalized.replace(/\s+/g, '') : normalized;
};

const normalizeLicensePlateIdentity = (value) => {
    const normalized = normalizeSearchText(value);
    const numbers = (normalized.match(/[0-9]+/g) || []).join('');
    const letters = (normalized.match(/[a-z\u0600-\u06FF]+/g) || []).join('');
    return [numbers, letters].filter(Boolean).join('|');
};

const getCombinedVehicleName = (vehicle = {}) => (
    [vehicle.make, vehicle.model].filter(Boolean).join(' ').trim()
);

const getVehicleFilterNames = (vehicleFilter = {}) => (
    [
        vehicleFilter.vehicle_name,
        vehicleFilter.car_name,
        vehicleFilter.vehicle_label,
        getCombinedVehicleName(vehicleFilter),
        vehicleFilter.make,
        vehicleFilter.model,
    ]
        .map((value) => normalizeIdentityValue(value))
        .filter(Boolean)
);

const getRowVehicleNames = (row = {}) => (
    [
        row.vehicle_name,
        row.car_name,
        row.vehicle_label,
        getCombinedVehicleName(row),
        row.make,
        row.model,
    ]
        .map((value) => normalizeIdentityValue(value))
        .filter(Boolean)
);

const hasVehicleIdentityFilter = (vehicleFilter = {}) => (
    isPresent(vehicleFilter.chassis_number)
    || isPresent(vehicleFilter.license_plate)
    || getVehicleFilterNames(vehicleFilter).length > 0
);

const parseNumber = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const text = compactWhitespace(value).replace(/,/g, '');
    if (!text) return null;
    const numericValue = Number(text);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const parseSortableNumber = (value) => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;
    const text = compactWhitespace(value)
        .replace(/,/g, '')
        .replace(/%$/g, '')
        .replace(/^egp\s*/i, '')
        .replace(/\s*(wh\/km|kwh|km|kg)$/i, '');
    if (!text) return null;
    const numericValue = Number(text);
    return Number.isFinite(numericValue) ? numericValue : null;
};

const parseDateValue = (value) => {
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;

    const text = compactWhitespace(value);
    if (!text) return null;

    const localDateMatch = text.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (localDateMatch) {
        const [, year, month, day] = localDateMatch.map(Number);
        return new Date(year, month - 1, day);
    }

    const date = new Date(text);
    return Number.isNaN(date.getTime()) ? null : date;
};

const isBooleanText = (value) => ['true', 'false'].includes(String(value).trim().toLowerCase());

const matchesAny = (column, patterns) => patterns.some((pattern) => pattern.test(column));

const formatTemplate = (template, values = {}) => (
    String(template || '').replace(/\{(\w+)\}/g, (match, key) => (
        values[key] !== undefined ? values[key] : match
    ))
);

const DETAIL_DISPLAY = 'detail';
const SUMMARY_DISPLAY = 'summary';

const ZERO_DECIMAL_OPTIONS = { maximumFractionDigits: 0 };
const ONE_DECIMAL_OPTIONS = { maximumFractionDigits: 1 };
const TWO_DECIMAL_OPTIONS = { maximumFractionDigits: 2 };
const COMPACT_SUMMARY_OPTIONS = {
    notation: 'compact',
    maximumFractionDigits: 1,
};

const formatNumber = (value, options = {}, locale) => value.toLocaleString(locale || undefined, {
    maximumFractionDigits: 2,
    ...options,
});

const getDisplayContext = ({ display, displayContext } = {}) => (
    displayContext || display || DETAIL_DISPLAY
);

const isSummaryDisplay = (display) => display === SUMMARY_DISPLAY;

const getCompactOptions = (options = {}) => ({
    ...COMPACT_SUMMARY_OPTIONS,
    ...options,
});

const getTotalMagnitudeOptions = (numericValue) => (
    Math.abs(numericValue) > 0 && Math.abs(numericValue) < 10
        ? ONE_DECIMAL_OPTIONS
        : ZERO_DECIMAL_OPTIONS
);

const getCalendarBucketNumberOptions = (display) => (
    isSummaryDisplay(display)
        ? getCompactOptions({ maximumFractionDigits: 0, useGrouping: false })
        : { maximumFractionDigits: 0, useGrouping: false }
);

const getCountNumberOptions = (display) => (
    isSummaryDisplay(display) ? getCompactOptions() : ZERO_DECIMAL_OPTIONS
);

const getCurrencyNumberOptions = (display) => (
    isSummaryDisplay(display) ? getCompactOptions() : ZERO_DECIMAL_OPTIONS
);

const getTotalMetricNumberOptions = (numericValue, display) => (
    isSummaryDisplay(display) ? getCompactOptions() : getTotalMagnitudeOptions(numericValue)
);

const formatPercent = (percentValue, locale) => {
    if (percentValue !== 0 && Math.abs(percentValue) < 0.1) {
        const threshold = `${formatNumber(0.1, ONE_DECIMAL_OPTIONS, locale)}%`;
        return percentValue < 0 ? `>-${threshold}` : `<${threshold}`;
    }

    return `${formatNumber(percentValue, ONE_DECIMAL_OPTIONS, locale)}%`;
};

const getStatusLabel = (status, copy = DEFAULT_FORMAT_COPY) => {
    const normalizedStatus = String(status || 'unknown');
    return copy.statuses?.[normalizedStatus]
        || DEFAULT_FORMAT_COPY.statuses[normalizedStatus]
        || formatColumnLabel(normalizedStatus, copy.columnLabels);
};

const withCopyDefaults = (copy = {}) => ({
    ...DEFAULT_FORMAT_COPY,
    ...copy,
    booleans: {
        ...DEFAULT_FORMAT_COPY.booleans,
        ...(copy.booleans || {}),
    },
    units: {
        ...DEFAULT_FORMAT_COPY.units,
        ...(copy.units || {}),
    },
    summary: {
        ...DEFAULT_FORMAT_COPY.summary,
        ...(copy.summary || {}),
    },
    statuses: {
        ...DEFAULT_FORMAT_COPY.statuses,
        ...(copy.statuses || {}),
    },
    table: {
        ...DEFAULT_FORMAT_COPY.table,
        ...(copy.table || {}),
    },
    impact: {
        ...DEFAULT_FORMAT_COPY.impact,
        ...(copy.impact || {}),
    },
});

export const formatColumnLabel = (column, columnLabels = {}) => {
    if (columnLabels[column]) return columnLabels[column];
    const compactColumn = compactWhitespace(column);
    if (columnLabels[compactColumn]) return columnLabels[compactColumn];

    const normalizedColumn = normalizeColumnLabelKey(column);
    const translatedMatch = Object.entries(columnLabels).find(([labelKey]) => (
        normalizeColumnLabelKey(labelKey) === normalizedColumn
    ));
    if (translatedMatch) return translatedMatch[1];

    if (COLUMN_LABELS[column]) return COLUMN_LABELS[column];
    if (COLUMN_LABELS[compactColumn]) return COLUMN_LABELS[compactColumn];

    const defaultMatch = Object.entries(COLUMN_LABELS).find(([labelKey]) => (
        normalizeColumnLabelKey(labelKey) === normalizedColumn
    ));
    if (defaultMatch) return defaultMatch[1];

    return compactColumn
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const formatAnalysisTimestamp = (value, locale) => {
    if (!value) return '--';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString(locale || undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

export const getAnalysisSummaryStats = (result, { showCacheDetails = true, copy = {}, locale } = {}) => {
    if (!result) return [];
    const mergedCopy = withCopyDefaults(copy);

    const stats = [
        {
            label: mergedCopy.summary.rows,
            value: Number(result.rowCount || result.rows?.length || 0).toLocaleString(locale || undefined),
        },
    ];

    if (showCacheDetails) {
        stats.push({
            label: mergedCopy.summary.cache,
            value: getStatusLabel(result.isStale ? 'stale' : result.cacheStatus, mergedCopy),
        });
    }

    stats.push({
        label: mergedCopy.summary.refreshed,
        value: formatAnalysisTimestamp(result.refreshedAt, locale),
    });

    return stats;
};

export const getDefaultVisibleColumns = (analysisType, availableColumns = []) => {
    const availableSet = new Set(availableColumns);
    const configuredDefaults = DEFAULT_VISIBLE_COLUMNS[analysisType] || [];
    const defaults = VEHICLE_LEVEL_ANALYSIS_TYPES.has(analysisType) && !availableSet.has('vehicle_name') && availableSet.has('car_name')
        ? ['car_name', ...configuredDefaults]
        : configuredDefaults;
    const visibleDefaults = defaults.filter((column) => availableSet.has(column));

    if (visibleDefaults.length > 0) return visibleDefaults;
    return availableColumns.slice(0, 8);
};

export const dedupeVisibleColumns = (selectedColumns = []) => {
    const seenColumns = new Set();
    return (selectedColumns || []).filter((column) => {
        if (seenColumns.has(column)) return false;
        seenColumns.add(column);
        return true;
    });
};

export const sanitizeVisibleColumns = (availableColumns = [], selectedColumns = []) => {
    const availableSet = new Set(availableColumns);
    return dedupeVisibleColumns(selectedColumns).filter((column) => availableSet.has(column));
};

export const resolveVisibleColumns = ({ analysisType, availableColumns = [], selectedColumns = [] }) => {
    const selected = sanitizeVisibleColumns(availableColumns, selectedColumns);
    return selected.length > 0 ? selected : getDefaultVisibleColumns(analysisType, availableColumns);
};

export const getColumnSelectionStorageKey = (analysisType) => (
    `${COLUMN_SELECTION_STORAGE_PREFIX}.${analysisType}${COLUMN_SELECTION_STORAGE_VERSION[analysisType] ? `.${COLUMN_SELECTION_STORAGE_VERSION[analysisType]}` : ''}`
);

export const getColumnWidthStorageKey = (analysisType) => (
    `${COLUMN_WIDTH_STORAGE_PREFIX}.${analysisType}${COLUMN_WIDTH_STORAGE_VERSION[analysisType] ? `.${COLUMN_WIDTH_STORAGE_VERSION[analysisType]}` : ''}`
);

export const clampColumnWidth = (width) => {
    const numericWidth = Number(width);
    if (!Number.isFinite(numericWidth)) return null;
    return Math.min(COLUMN_WIDTH_LIMITS.MAX, Math.max(COLUMN_WIDTH_LIMITS.MIN, Math.round(numericWidth)));
};

export const sanitizeColumnWidths = (availableColumns = [], widths = {}) => {
    if (!widths || typeof widths !== 'object' || Array.isArray(widths)) return {};

    const availableSet = new Set(availableColumns);
    return Object.entries(widths).reduce((sanitizedWidths, [column, width]) => {
        if (!availableSet.has(column)) return sanitizedWidths;
        const clampedWidth = clampColumnWidth(width);
        if (clampedWidth === null) return sanitizedWidths;
        return {
            ...sanitizedWidths,
            [column]: clampedWidth,
        };
    }, {});
};

export const resolveColumnWidths = (columns = [], storedWidths = {}) => (
    (columns || []).reduce((resolvedWidths, column) => ({
        ...resolvedWidths,
        [column]: storedWidths[column] ?? getColumnPreferredWidth(column),
    }), {})
);

export const readStoredVisibleColumns = (analysisType, availableColumns = [], storage) => {
    if (!storage) return null;

    try {
        const storedValue = storage.getItem(getColumnSelectionStorageKey(analysisType));
        if (!storedValue) return null;

        const parsedColumns = JSON.parse(storedValue);
        if (!Array.isArray(parsedColumns)) return null;

        const columns = sanitizeVisibleColumns(availableColumns, parsedColumns);
        return columns.length > 0 ? columns : null;
    } catch {
        return null;
    }
};

export const writeStoredVisibleColumns = (analysisType, visibleColumns, storage) => {
    if (!storage) return;

    try {
        storage.setItem(getColumnSelectionStorageKey(analysisType), JSON.stringify(dedupeVisibleColumns(visibleColumns)));
    } catch {
        // Local storage can be disabled; column changes should still work for this session.
    }
};

export const clearStoredVisibleColumns = (analysisType, storage) => {
    if (!storage) return;

    try {
        storage.removeItem(getColumnSelectionStorageKey(analysisType));
    } catch {
        // Ignore storage failures.
    }
};

export const readStoredColumnWidths = (analysisType, availableColumns = [], storage) => {
    if (!storage) return {};

    try {
        const storedValue = storage.getItem(getColumnWidthStorageKey(analysisType));
        if (!storedValue) return {};

        const parsedWidths = JSON.parse(storedValue);
        return sanitizeColumnWidths(availableColumns, parsedWidths);
    } catch {
        return {};
    }
};

export const writeStoredColumnWidths = (analysisType, columnWidths, storage) => {
    if (!storage) return;

    try {
        storage.setItem(getColumnWidthStorageKey(analysisType), JSON.stringify(sanitizeColumnWidths(Object.keys(columnWidths || {}), columnWidths)));
    } catch {
        // Local storage can be disabled; column resizing should still work for this session.
    }
};

export const clearStoredColumnWidths = (analysisType, storage) => {
    if (!storage) return;

    try {
        storage.removeItem(getColumnWidthStorageKey(analysisType));
    } catch {
        // Ignore storage failures.
    }
};

const formatWithUnit = (numericValue, unit, locale, numberOptions = {}) => (
    `${formatNumber(numericValue, numberOptions, locale)} ${unit}`
);

const formatCurrency = (numericValue, { units, locale, display }) => (
    `${units.currencyPrefix} ${formatNumber(numericValue, getCurrencyNumberOptions(display), locale)}`
);

const formatCurrencyPerKm = (numericValue, { units, locale }) => (
    `${units.currencyPrefix} ${formatNumber(numericValue, TWO_DECIMAL_OPTIONS, locale)}/${units.km}`
);

const isCalendarBucketColumn = (columnName) => {
    const normalizedColumn = getNormalizedColumnKey(columnName);
    return ['year', 'years', 'yearly', 'month', 'months', 'monthly', 'quarter', 'quarterly'].includes(normalizedColumn);
};

const isOneDecimalMetricColumn = (columnName) => (
    matchesAny(columnName, ONE_DECIMAL_METRIC_COLUMN_PATTERNS)
);

const isTotalMagnitudeColumn = (columnName) => (
    matchesAny(columnName, TOTAL_MAGNITUDE_COLUMN_PATTERNS)
);

const getDistanceNumberOptions = (columnName, numericValue, display) => {
    if (isSummaryDisplay(display)) return getCompactOptions();
    return isOneDecimalMetricColumn(columnName)
        ? ONE_DECIMAL_OPTIONS
        : getTotalMagnitudeOptions(numericValue);
};

const getEnergyNumberOptions = (columnName, numericValue, display) => {
    if (isSummaryDisplay(display)) return getCompactOptions();
    return isTotalMagnitudeColumn(columnName)
        ? getTotalMagnitudeOptions(numericValue)
        : ONE_DECIMAL_OPTIONS;
};

export const formatCellValue = (column, value, { copy = {}, locale, display, displayContext } = {}) => {
    if (!isPresent(value)) return '--';
    const mergedCopy = withCopyDefaults(copy);
    const columnName = compactWhitespace(column);
    const resolvedDisplay = getDisplayContext({ display, displayContext });

    if (isBooleanText(value)) {
        return String(value).trim().toLowerCase() === 'true'
            ? mergedCopy.booleans.yes
            : mergedCopy.booleans.no;
    }

    if (columnName === 'behavior_status') {
        return getStatusLabel(value, mergedCopy);
    }

    if (TEXT_VALUE_COLUMNS.has(column)) return String(value);

    if (matchesAny(columnName, DATE_COLUMN_PATTERNS)) {
        const dateValue = parseDateValue(value);
        if (dateValue) {
            return dateValue.toLocaleDateString(locale || undefined, {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
            });
        }
    }

    const numericValue = parseNumber(value);
    if (numericValue === null) return String(value);

    if (isCalendarBucketColumn(columnName)) {
        return formatNumber(numericValue, getCalendarBucketNumberOptions(resolvedDisplay), locale);
    }

    if (matchesAny(columnName, PERCENT_COLUMN_PATTERNS)) {
        const percentValue = /(usage rate|ratio)/i.test(columnName) && Math.abs(numericValue) <= 1
            ? numericValue * 100
            : numericValue;
        return formatPercent(percentValue, locale);
    }

    if (matchesAny(columnName, CURRENCY_COLUMN_PATTERNS)) {
        return formatCurrency(numericValue, { units: mergedCopy.units, locale, display: resolvedDisplay });
    }

    if (matchesAny(columnName, ENERGY_COLUMN_PATTERNS)) {
        return formatWithUnit(
            numericValue,
            mergedCopy.units.kwh,
            locale,
            getEnergyNumberOptions(columnName, numericValue, resolvedDisplay)
        );
    }

    if (matchesAny(columnName, EV_CONSUMPTION_RATE_COLUMN_PATTERNS)) {
        return formatWithUnit(
            numericValue,
            mergedCopy.units.whPerKm,
            locale,
            isSummaryDisplay(resolvedDisplay) ? getCompactOptions() : ONE_DECIMAL_OPTIONS
        );
    }

    if (matchesAny(columnName, COST_PER_KM_COLUMN_PATTERNS)) {
        return formatCurrencyPerKm(numericValue, { units: mergedCopy.units, locale });
    }

    if (matchesAny(columnName, CO2_COLUMN_PATTERNS)) {
        return formatWithUnit(
            numericValue,
            mergedCopy.units.kg,
            locale,
            getTotalMetricNumberOptions(numericValue, resolvedDisplay)
        );
    }

    if (matchesAny(columnName, DISTANCE_COLUMN_PATTERNS)) {
        return formatWithUnit(
            numericValue,
            mergedCopy.units.km,
            locale,
            getDistanceNumberOptions(columnName, numericValue, resolvedDisplay)
        );
    }

    if (matchesAny(columnName, COUNT_COLUMN_PATTERNS)) {
        return formatNumber(numericValue, getCountNumberOptions(resolvedDisplay), locale);
    }

    if (isOneDecimalMetricColumn(columnName)) {
        return formatNumber(numericValue, ONE_DECIMAL_OPTIONS, locale);
    }

    return formatNumber(numericValue, TWO_DECIMAL_OPTIONS, locale);
};

const getResultColumn = (result, targetColumn) => {
    const targetKey = normalizeColumnLabelKey(targetColumn);
    return (result?.columns || []).find((column) => normalizeColumnLabelKey(column) === targetKey) || '';
};

const sumResultColumn = (result, targetColumn) => {
    const column = getResultColumn(result, targetColumn);
    if (!column) return null;

    let total = 0;
    let hasNumericValue = false;
    (result?.rows || []).forEach((row) => {
        const numericValue = parseNumber(row?.[column]);
        if (numericValue === null) return;
        total += numericValue;
        hasNumericValue = true;
    });

    return hasNumericValue ? { column, value: total } : null;
};

const getSummaryActuals = (summaryResult) => (
    summaryResult?.payload?.impact_summary?.actuals
    || summaryResult?.impact_summary?.actuals
    || null
);

const getSummaryActualMetric = (summaryResult, metricKey) => {
    const actuals = getSummaryActuals(summaryResult);
    const metric = actuals?.[metricKey];
    if (!metric || typeof metric !== 'object') return null;

    const numericValue = parseNumber(metric.value);
    if (numericValue === null) return null;
    return {
        value: numericValue,
        column: metric.source_column || '',
    };
};

const getImpactMetricAggregate = ({ summaryResult, allVehiclesResult, metric }) => {
    const summaryMetric = getSummaryActualMetric(summaryResult, metric.actualKey);
    if (summaryMetric) {
        return {
            column: summaryMetric.column || metric.fallbackColumn,
            value: summaryMetric.value,
        };
    }
    return sumResultColumn(allVehiclesResult, metric.fallbackColumn);
};

export const getOperatingImpactMetrics = ({ summaryResult, allVehiclesResult, copy = {}, locale } = {}) => {
    const mergedCopy = withCopyDefaults(copy);

    return IMPACT_METRIC_COLUMNS.flatMap((metric) => {
        const aggregate = getImpactMetricAggregate({
            summaryResult,
            allVehiclesResult,
            metric,
        });
        if (!aggregate) return [];

        return [{
            key: metric.key,
            label: mergedCopy.impact[metric.copyKey] || metric.fallbackLabel,
            column: aggregate.column,
            value: aggregate.value,
            formattedValue: formatCellValue(aggregate.column, aggregate.value, {
                copy: mergedCopy,
                locale,
                display: SUMMARY_DISPLAY,
            }),
            detailFormattedValue: formatCellValue(aggregate.column, aggregate.value, { copy: mergedCopy, locale }),
        }];
    });
};

const addOpportunitySummaryValue = (detail, fallbackColumn, copy, locale) => {
    if (!detail || detail.isUnavailable || detail.value === null || detail.value === undefined) {
        return detail;
    }

    const column = detail.column || fallbackColumn;
    return {
        ...detail,
        summaryFormattedValue: formatCellValue(column, detail.value, {
            copy,
            locale,
            display: SUMMARY_DISPLAY,
        }),
    };
};

const replaceOpportunityActualSavings = ({
    opportunity,
    summaryResult,
    copy,
    locale,
}) => {
    const summaryActualSavings = getSummaryActualMetric(summaryResult, 'actual_savings_egp');
    if (!opportunity || !summaryActualSavings) return opportunity;

    const column = summaryActualSavings.column || OPPORTUNITY_DETAIL_COLUMNS.actualSavings;
    const actualSavings = {
        ...(opportunity.actualSavings || {}),
        label: opportunity.actualSavings?.label || copy.impact.actualSavings,
        column,
        value: summaryActualSavings.value,
        formattedValue: formatCellValue(column, summaryActualSavings.value, { copy, locale }),
    };

    return {
        ...opportunity,
        actualSavings,
    };
};

export const getOperatingOpportunitySummary = ({ summaryResult, allVehiclesResult, copy = {}, locale } = {}) => {
    const mergedCopy = withCopyDefaults(copy);
    const opportunity = replaceOpportunityActualSavings({
        opportunity: getNormalizedOperatingOpportunitySummary({
            allVehiclesResult,
            copy: mergedCopy,
            locale,
            formatCellValue,
        }),
        summaryResult,
        copy: mergedCopy,
        locale,
    });

    if (!opportunity) return opportunity;

    return {
        ...opportunity,
        actualSavings: addOpportunitySummaryValue(
            opportunity.actualSavings,
            OPPORTUNITY_DETAIL_COLUMNS.actualSavings,
            mergedCopy,
            locale
        ),
        maxOperatingSavings: addOpportunitySummaryValue(
            opportunity.maxOperatingSavings,
            OPPORTUNITY_DETAIL_COLUMNS.maxOperatingSavings,
            mergedCopy,
            locale
        ),
        additionalOpportunity: addOpportunitySummaryValue(
            opportunity.additionalOpportunity,
            OPPORTUNITY_DETAIL_COLUMNS.additionalOpportunity,
            mergedCopy,
            locale
        ),
        operatingVehiclesBasis: addOpportunitySummaryValue(
            opportunity.operatingVehiclesBasis,
            OPPORTUNITY_DETAIL_COLUMNS.operatingVehiclesBasis,
            mergedCopy,
            locale
        ),
    };
};

export const getOperatingImpactSummary = ({ results = {}, copy = {}, locale } = {}) => ({
    cards: getOperatingImpactMetrics({
        summaryResult: results.per_vehicle_summary,
        allVehiclesResult: results.all_vehicles,
        copy,
        locale,
    }),
    opportunity: getOperatingOpportunitySummary({
        summaryResult: results.per_vehicle_summary,
        allVehiclesResult: results.all_vehicles,
        copy,
        locale,
    }),
});

export const getColumnAlignmentClass = (column, value) => {
    if (isTextColumn(column)) return '';
    if (isMetricColumn(column)) return 'operating-analysis-cell-number';
    return value !== undefined && parseNumber(value) !== null && !isBooleanText(value)
        ? 'operating-analysis-cell-number'
        : '';
};

export const getColumnLayoutClass = (column) => (
    `operating-analysis-column-${getColumnLayoutType(column)}`
);

export const getColumnPreferredWidth = (column) => (
    COLUMN_PREFERRED_WIDTHS[getColumnLayoutType(column)] || COLUMN_PREFERRED_WIDTHS.standard
);

export const getPaginationRange = ({ page = 1, pageSize = DEFAULT_PAGE_SIZE, total = 0 }) => {
    const safePageSize = PAGE_SIZE_OPTIONS.includes(Number(pageSize)) ? Number(pageSize) : DEFAULT_PAGE_SIZE;
    const totalPages = Math.max(1, Math.ceil(total / safePageSize));
    const safePage = Math.min(Math.max(Number(page) || 1, 1), totalPages);
    const startIndex = total === 0 ? 0 : (safePage - 1) * safePageSize;
    const endIndex = total === 0 ? 0 : Math.min(startIndex + safePageSize, total);

    return {
        page: safePage,
        pageSize: safePageSize,
        totalPages,
        startIndex,
        endIndex,
    };
};

export const paginateRows = (rows = [], page, pageSize) => {
    const range = getPaginationRange({ page, pageSize, total: rows.length });
    return rows.slice(range.startIndex, range.endIndex);
};

const hasMonthSearchTerm = (searchQuery) => MONTH_SEARCH_PATTERN.test(normalizeSearchText(searchQuery));

export const doesRowMatchSearchQuery = (row = {}, searchQuery = '', { columns = [], copy = {}, locale } = {}) => {
    if (matchesSearchQuery([], searchQuery)) return true;

    const rawValues = Object.values(row || {});
    const formattedValues = columns.map((column) => formatCellValue(column, row?.[column], { copy, locale }));
    const searchableValues = [...rawValues, ...formattedValues];

    if (hasMonthSearchTerm(searchQuery)) {
        return searchableValues.some((value) => matchesSearchQuery([value], searchQuery));
    }

    return matchesSearchQuery(searchableValues, searchQuery);
};

export const filterRowsBySearchQuery = (rows = [], searchQuery = '') => (
    (rows || []).filter((row) => doesRowMatchSearchQuery(row, searchQuery))
);

export const doesRowMatchVehicleIdentity = (row = {}, vehicleFilter = null, analysisType = 'per_vehicle') => {
    if (!VEHICLE_LEVEL_ANALYSIS_TYPES.has(analysisType) || !vehicleFilter || !hasVehicleIdentityFilter(vehicleFilter)) {
        return true;
    }

    const filterChassis = normalizeIdentityValue(vehicleFilter.chassis_number);
    const filterLicensePlate = normalizeLicensePlateIdentity(vehicleFilter.license_plate);
    const filterVehicleNames = getVehicleFilterNames(vehicleFilter);

    const rowChassis = normalizeIdentityValue(row?.chassis_number);
    if (filterChassis && rowChassis) return rowChassis === filterChassis;

    const rowLicensePlate = normalizeLicensePlateIdentity(row?.license_plate);
    if (filterLicensePlate && rowLicensePlate) return rowLicensePlate === filterLicensePlate;

    const rowVehicleNames = getRowVehicleNames(row);
    if (filterVehicleNames.length > 0 && rowVehicleNames.length > 0) {
        return filterVehicleNames.some((filterName) => rowVehicleNames.includes(filterName));
    }

    return false;
};

export const filterRowsByVehicleIdentity = (rows = [], vehicleFilter = null, analysisType = 'per_vehicle') => {
    if (!VEHICLE_LEVEL_ANALYSIS_TYPES.has(analysisType) || !vehicleFilter || !hasVehicleIdentityFilter(vehicleFilter)) {
        return rows || [];
    }

    return (rows || []).filter((row) => doesRowMatchVehicleIdentity(row, vehicleFilter, analysisType));
};

const getSortableValue = (column, value) => {
    if (!isPresent(value)) return { type: 'empty', value: null };

    if (isBooleanText(value)) {
        return {
            type: 'number',
            value: String(value).trim().toLowerCase() === 'true' ? 1 : 0,
        };
    }

    const columnName = compactWhitespace(column);
    if (matchesAny(columnName, DATE_COLUMN_PATTERNS)) {
        const dateValue = parseDateValue(value);
        if (dateValue) return { type: 'date', value: dateValue.getTime() };
    }

    const numericValue = parseSortableNumber(value);
    if (numericValue !== null) return { type: 'number', value: numericValue };

    return {
        type: 'text',
        value: normalizeSearchText(value),
    };
};

export const compareAnalysisValues = (column, leftValue, rightValue, direction = SORT_DIRECTIONS.ASC) => {
    const left = getSortableValue(column, leftValue);
    const right = getSortableValue(column, rightValue);

    if (left.type === 'empty' || right.type === 'empty') {
        if (left.type === right.type) return 0;
        return left.type === 'empty' ? 1 : -1;
    }

    let comparison = 0;
    if ((left.type === 'number' || left.type === 'date') && left.type === right.type) {
        comparison = left.value === right.value ? 0 : left.value > right.value ? 1 : -1;
    } else {
        comparison = String(left.value).localeCompare(String(right.value), undefined, {
            numeric: true,
            sensitivity: 'base',
        });
    }

    return direction === SORT_DIRECTIONS.DESC ? comparison * -1 : comparison;
};

export const sortRowsByColumn = (rows = [], sortState = {}, options = {}) => {
    const { column, direction } = sortState || {};
    if (!column || !Object.values(SORT_DIRECTIONS).includes(direction)) return rows || [];

    const getRow = options.getRow || ((row) => row);
    const getStableIndex = options.getStableIndex || ((row, index) => index);

    return [...(rows || [])]
        .map((item, index) => ({ item, stableIndex: getStableIndex(item, index) }))
        .sort((left, right) => {
            const comparison = compareAnalysisValues(
                column,
                getRow(left.item)?.[column],
                getRow(right.item)?.[column],
                direction
            );
            return comparison || left.stableIndex - right.stableIndex;
        })
        .map(({ item }) => item);
};

export const getAnalysisRowKey = ({ analysisType = 'analysis', row = {}, rowIndex = 0 } = {}) => {
    const identity = [
        row?.chassis_number,
        row?.license_plate,
        row?.vehicle_name || row?.car_name || row?.vehicle_model,
        row?.date,
    ]
        .map((value) => normalizeSearchText(value).replace(/\s+/g, ''))
        .filter(Boolean)
        .join('|');

    return `${analysisType}:${rowIndex}:${identity || 'row'}`;
};

export const getPaginationSummary = ({ page = 1, pageSize = DEFAULT_PAGE_SIZE, total = 0, copy = {}, locale }) => {
    const mergedCopy = withCopyDefaults(copy);
    const formatPaginationNumber = (value) => Number(value).toLocaleString(locale || undefined);
    if (total === 0) return mergedCopy.table.showingEmpty;
    const range = getPaginationRange({ page, pageSize, total });
    return formatTemplate(mergedCopy.table.showingRange, {
        start: formatPaginationNumber(range.startIndex + 1),
        end: formatPaginationNumber(range.endIndex),
        total: formatPaginationNumber(total),
    });
};
